import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { MapPin, Eye, ImageOff } from 'lucide-react';
import { resolveAssetUrl } from '../lib/assets';
import { regionLabel } from '../lib/regionNames';
import { CLASSIFIED_CATEGORY_MAP, resolveCat, formatClassifiedPrice } from '../data/classifiedsCategories';

const STATUS_BADGE = {
  sold: { bg: 'bg-[#121212]/80', lv: 'Pārdots', en: 'Sold', ru: 'Продано' },
};

export default function ClassifiedCard({ item, catMap }) {
  const { tr } = useLanguage();
  const cat = resolveCat(item.category, catMap);
  const img = item.images && item.images.length ? resolveAssetUrl(item.images[0]) : null;
  const sold = item.status === 'sold';

  return (
    <Link
      to={`/sludinajums/${item.slug || item.id}`}
      className="group block bg-white border border-[#E5E4E0] rounded-2xl overflow-hidden hover:border-[#2F7E79] hover:shadow-[0_8px_30px_rgba(47,126,121,0.12)] transition-all duration-300"
      data-testid={`classified-card-${item.id}`}
    >
      <div className="relative aspect-[4/3] bg-[#F3F2EE] overflow-hidden">
        {img ? (
          <img src={img} alt={item.title} loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#C7C5BF]"><ImageOff className="w-8 h-8" /></div>
        )}
        {cat && (
          <span className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-white/90 backdrop-blur text-[11px] font-semibold text-[#121212] shadow-sm">
            <span>{cat.emoji}</span> {tr(cat.lv, cat.en, cat.ru)}
          </span>
        )}
        {sold && (
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="px-4 py-1.5 rounded-full bg-[#121212]/80 text-white text-sm font-bold uppercase tracking-wide">{tr('Pārdots', 'Sold', 'Продано')}</span>
          </span>
        )}
        {item.images && item.images.length > 1 && (
          <span className="absolute bottom-2.5 right-2.5 h-6 px-2 rounded-full bg-black/55 text-white text-[11px] font-medium flex items-center">{item.images.length} 📷</span>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-outfit font-semibold text-[#121212] leading-snug line-clamp-2 group-hover:text-[#2F7E79] transition-colors" data-testid="classified-card-title">{item.title}</h3>
        <div className="mt-2 text-lg font-bold text-[#2F7E79]" data-testid="classified-card-price">{formatClassifiedPrice(item, tr)}</div>
        <div className="mt-2 flex items-center gap-3 text-xs text-[#8A8A8A]">
          {item.region && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {item.location_name || regionLabel(item.region)}</span>}
          <span className="inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {item.views || 0}</span>
        </div>
      </div>
    </Link>
  );
}
